import { useState } from 'react';
import { Modal, Button, Alert, Checkbox, Typography, Space, Spin } from 'antd';
import { ExclamationCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../api';

const { Text, Paragraph } = Typography;

interface AccountClosureProps {
  onStatusChange?: () => void;
}

export function AccountClosure({ onStatusChange }: AccountClosureProps) {
  const { t } = useTranslation('dashboard');
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleRequestClosure = async () => {
    if (!confirmed) {
      return;
    }

    try {
      setSubmitting(true);
      await api.users.requestAccountClosure();
      
      Modal.success({
        title: t('accountClosure.successTitle', 'Request Submitted'),
        content: t(
          'accountClosure.successMessage',
          'Your account closure request has been submitted and is pending admin approval. You will no longer receive new profit allocations.'
        ),
        onOk: () => {
          setModalVisible(false);
          if (onStatusChange) {
            onStatusChange();
          }
        },
      });
    } catch (error: any) {
      Modal.error({
        title: t('accountClosure.errorTitle', 'Request Failed'),
        content: error.message || t('accountClosure.errorMessage', 'Failed to submit closure request. Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        danger
        icon={<CloseCircleOutlined />}
        onClick={() => setModalVisible(true)}
      >
        {t('accountClosure.requestButton', 'Request Account Closure')}
      </Button>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>{t('accountClosure.modalTitle', 'Request Account Closure')}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          if (!submitting) {
            setModalVisible(false);
            setConfirmed(false);
          }
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setModalVisible(false);
              setConfirmed(false);
            }}
            disabled={submitting}
          >
            {t('accountClosure.cancelButton', 'Cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleRequestClosure}
            disabled={!confirmed || submitting}
            loading={submitting}
          >
            {t('accountClosure.confirmButton', 'Confirm Closure Request')}
          </Button>,
        ]}
        width={600}
      >
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message={t('accountClosure.warningTitle', 'Important: Please Read Carefully')}
            description={
              <Space orientation="vertical" size="small">
                <Text>{t('accountClosure.warningMessage', 'Requesting account closure means:')}</Text>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>
                    {t(
                      'accountClosure.warningPoint1',
                      'You will be removed from future profit allocations after your next weekly settlement.'
                    )}
                  </li>
                  <li>
                    {t(
                      'accountClosure.warningPoint2',
                      'Your base principal will be set to $0 and removed from the pool.'
                    )}
                  </li>
                  <li>
                    {t(
                      'accountClosure.warningPoint3',
                      'A final withdrawal request will be automatically created for your entire withdrawable balance.'
                    )}
                  </li>
                  <li>
                    {t(
                      'accountClosure.warningPoint4',
                      'You can still log in to view your historical records after closure.'
                    )}
                  </li>
                  <li>
                    {t(
                      'accountClosure.warningPoint5',
                      'This action cannot be easily reversed once the final settlement is processed.'
                    )}
                  </li>
                </ul>
              </Space>
            }
            type="warning"
            showIcon
          />

          <Paragraph>
            {t(
              'accountClosure.explanation',
              'If you proceed, you will continue to receive one more profit allocation in the upcoming weekly settlement. After that settlement completes, the system will automatically create a final withdrawal for your entire withdrawable balance, and you will no longer participate in future profit distributions.'
            )}
          </Paragraph>

          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          >
            <strong>
              {t(
                'accountClosure.confirmCheckbox',
                'I understand and want to close my account and exit the pool'
              )}
            </strong>
          </Checkbox>
        </Space>
      </Modal>
    </>
  );
}
